import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analystAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_META = {
  trade: { label: 'TRADE', color: 'var(--ft-brand-ink)', bg: 'var(--ft-brand-soft)', border: 'rgba(255,122,0,0.3)' },
  hypothetical: { label: 'HYPOTHETICAL', color: '#005FAA', bg: 'rgba(0,95,170,0.08)', border: 'rgba(0,95,170,0.22)' },
  question: { label: 'QUESTION', color: '#6A3D9A', bg: 'rgba(106,61,154,0.08)', border: 'rgba(106,61,154,0.22)' },
  executed: { label: 'TRADE', color: 'var(--ft-brand-ink)', bg: 'var(--ft-brand-soft)', border: 'rgba(255,122,0,0.3)' },
  roster: { label: 'ROSTER REVIEW', color: 'var(--ft-brand-ink)', bg: 'var(--ft-brand-soft)', border: 'rgba(255,122,0,0.3)' },
};

const STATUS_META = {
  awaiting_review: { label: 'NEEDS REVIEW', bg: 'var(--ft-dark)', color: '#fff' },
  completed: { label: 'DONE', bg: 'var(--ft-green-soft)', color: 'var(--ft-green-ink)', border: '1px solid rgba(52,199,89,0.25)' },
  expired: { label: 'OVERDUE', bg: 'rgba(255,59,48,0.08)', color: 'var(--ft-red)', border: '1px solid rgba(255,59,48,0.2)' },
  pending_payment: { label: 'AWAITING PAYMENT', bg: 'var(--ft-surface)', color: 'var(--ft-ink-3)' },
  refunded: { label: 'REFUNDED', bg: 'var(--ft-surface)', color: 'var(--ft-ink-3)' },
};

const getCountdown = (deadlineAt) => {
  if (!deadlineAt) return null;
  const diff = new Date(deadlineAt) - new Date();
  if (diff <= 0) return { label: 'OVERDUE', urgent: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 24) return { label: `${Math.floor(h / 24)}d ${h % 24}h`, urgent: false };
  return { label: h > 0 ? `${h}h ${m}m` : `${m}m`, urgent: h < 6 };
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : (name[0] || '?').toUpperCase();
};

const itemTypeKey = (item) =>
  item.type === 'hypothetical' && item.payload?.wasExecuted ? 'executed' : item.type;

const itemSummary = (item) => {
  if (item.type === 'trade') {
    const giving = (item.trade?.items || []).filter((i) => i.side === 'creator');
    const getting = (item.trade?.items || []).filter((i) => i.side === 'recipient');
    const g = giving.slice(0, 2).map((i) => i.playerName || (i.itemType === 'pick' ? `'${String(i.pickData?.season || '').slice(-2)} R${i.pickData?.round}` : '?')).join(', ');
    const r = getting.slice(0, 2).map((i) => i.playerName || (i.itemType === 'pick' ? `'${String(i.pickData?.season || '').slice(-2)} R${i.pickData?.round}` : '?')).join(', ');
    return g || r ? `${g || '—'} → ${r || '—'}` : 'Trade';
  }
  if (item.type === 'hypothetical') {
    const g = (item.payload?.giving || []).slice(0, 2).map((p) => p.name).join(', ');
    const r = (item.payload?.getting || []).slice(0, 2).map((p) => p.name).join(', ');
    return g || r ? `${g || '—'} → ${r || '—'}` : 'Hypothetical';
  }
  if (item.type === 'question') return (item.question || '').slice(0, 64) + ((item.question || '').length > 64 ? '…' : '');
  if (item.type === 'roster') return 'Full roster review';
  return 'Item';
};

const itemLeagueName = (item) => item.trade?.TradePackage?.leagueName || null;

// ─────────────────────────────────────────────────────────────────────────────
// Atoms
// ─────────────────────────────────────────────────────────────────────────────
const Avatar = ({ sleeperAvatar, name, size = 40 }) => {
  const [failed, setFailed] = useState(false);
  if (!sleeperAvatar || failed) {
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: 'var(--ft-surface)', border: '1px solid var(--ft-hairline)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: size * 0.34, fontWeight: 700, color: 'var(--ft-ink-2)',
        flexShrink: 0, userSelect: 'none',
      }}>{getInitials(name)}</div>
    );
  }
  return (
    <img
      src={`https://sleepercdn.com/avatars/thumbs/${sleeperAvatar}`}
      alt={name}
      onError={() => setFailed(true)}
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid var(--ft-hairline)' }}
    />
  );
};

const TypeBadge = ({ type }) => {
  const m = TYPE_META[type] || { label: type.toUpperCase(), color: 'var(--ft-ink-3)', bg: 'var(--ft-surface)', border: 'var(--ft-hairline)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 9, fontWeight: 800, letterSpacing: '1.2px',
      color: m.color, padding: '2px 7px', borderRadius: 4,
      background: m.bg, border: `1px solid ${m.border}`,
    }}>{m.label}</span>
  );
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || { label: status.toUpperCase(), bg: 'var(--ft-surface)', color: 'var(--ft-ink-3)' };
  return (
    <span style={{
      borderRadius: 'var(--ft-r-pill)', padding: '5px 12px',
      fontSize: 10, fontWeight: 800, letterSpacing: '0.8px', flexShrink: 0,
      background: m.bg, color: m.color,
      ...(m.border ? { border: m.border } : {}),
    }}>{m.label}</span>
  );
};

const Countdown = ({ deadline, status }) => {
  if (status !== 'awaiting_review') return null;
  const c = getCountdown(deadline);
  if (!c) return null;
  return (
    <span style={{
      fontSize: 12, fontWeight: 800,
      color: c.urgent ? 'var(--ft-red)' : 'var(--ft-ink-3)',
      letterSpacing: '0.5px',
    }}>{c.label}</span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Review row
// ─────────────────────────────────────────────────────────────────────────────
const ReviewRow = ({ review, onClick }) => {
  const requester = review.requestedBy;
  const items = review.items || [];
  const displayName = requester?.sleeperDisplayName || requester?.sleeperUsername || requester?.email || 'Unknown';
  const isUrgent = review.status === 'awaiting_review';
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '16px 20px',
        background: isUrgent ? 'var(--ft-bg)' : 'var(--ft-surface)',
        border: `1px solid ${isUrgent ? 'var(--ft-dark)' : hovered ? 'var(--ft-disabled)' : 'var(--ft-hairline)'}`,
        borderRadius: 14, marginBottom: 8, cursor: 'pointer',
        transform: hovered ? 'translateY(-1px)' : 'none',
        boxShadow: hovered ? 'var(--ft-shadow-m)' : 'none',
        transition: 'all 0.18s var(--ft-ease)',
      }}
    >
      <Avatar sleeperAvatar={requester?.sleeperAvatar} name={displayName} size={44} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ft-ink)', letterSpacing: '-0.005em' }}>{displayName}</span>
          {requester?.sleeperUsername && requester?.sleeperDisplayName !== requester?.sleeperUsername && (
            <span style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>@{requester.sleeperUsername}</span>
          )}
          <span style={{ fontSize: 11, color: 'var(--ft-ink-3)' }}>
            · {new Date(review.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        </div>
        {items.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {items.slice(0, 3).map((item, i) => (
              <div key={item.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TypeBadge type={itemTypeKey(item)} />
                <span style={{ fontSize: 12.5, color: 'var(--ft-ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                  {itemSummary(item)}
                </span>
                {itemLeagueName(item) && (
                  <span style={{ fontSize: 10.5, color: 'var(--ft-ink-3)', flexShrink: 0 }}>{itemLeagueName(item)}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--ft-ink-3)' }}>No items</span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
        <Countdown deadline={review.deadlineAt} status={review.status} />
        <StatusPill status={review.status} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
const AnalystDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    analystAPI.getQueue()
      .then((r) => setData(r.data))
      .catch((err) => setError(err?.response?.data?.error || 'Could not load queue.'))
      .finally(() => setLoading(false));
    analystAPI.getStripeStatus().then((r) => setStripeStatus(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'connected') {
      setStripeStatus((prev) => ({ ...prev, stripeOnboardingComplete: true }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user]);

  const handleStripeOnboard = async () => {
    setStripeLoading(true);
    try {
      const { data: { onboardingUrl } } = await analystAPI.startStripeOnboarding();
      window.location.href = onboardingUrl;
    } catch {
      setStripeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ft-view" style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
        <span style={{ color: 'var(--ft-ink-3)', fontSize: 15 }}>Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ft-view">
        <p style={{ color: 'var(--ft-red)', fontSize: 15 }}>{error}</p>
      </div>
    );
  }

  const reviews = data?.reviews || [];
  const pending = reviews.filter((r) => r.status === 'awaiting_review');
  const completed = reviews.filter((r) => r.status === 'completed');
  const expired = reviews.filter((r) => r.status === 'expired');
  const pendingPayment = reviews.filter((r) => r.status === 'pending_payment');
  const other = reviews.filter((r) => !['awaiting_review', 'completed', 'expired', 'pending_payment'].includes(r.status));
  const analystName = data?.analyst?.name || 'Analyst';

  const sections = [
    { label: 'Needs your attention', items: pending, urgent: true },
    { label: 'Completed', items: completed },
    { label: 'Awaiting payment', items: pendingPayment },
    { label: 'Overdue', items: expired },
    { label: 'Other', items: other },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="ft-view">
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <span className="ft-k">{analystName} · Analyst Studio</span>
        <h1 className="ft-hero" style={{ marginTop: 8, marginBottom: 6 }}>Your queue.</h1>
        <p className="ft-sub" style={{ marginBottom: 40 }}>
          {pending.length > 0
            ? `${pending.length} review${pending.length === 1 ? '' : 's'} waiting on you.`
            : "You're all caught up. Nothing pending."}
        </p>

        {stripeStatus && !stripeStatus.stripeOnboardingComplete && (
          <div style={{
            background: 'var(--ft-brand-soft)', border: '1px solid rgba(255,122,0,0.25)',
            borderLeft: '3px solid var(--ft-brand)',
            borderRadius: 14, padding: '18px 22px', marginBottom: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 16, flexWrap: 'wrap',
          }}>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ft-ink)', margin: '0 0 4px' }}>Connect your payouts.</p>
              <p style={{ fontSize: 13, color: 'var(--ft-ink-2)', margin: 0 }}>Set up Stripe to receive your 70% automatically.</p>
            </div>
            <button
              onClick={handleStripeOnboard}
              disabled={stripeLoading}
              className="ft-btn"
              style={{ flexShrink: 0 }}
            >
              {stripeLoading ? 'Redirecting…' : 'Set up payouts →'}
            </button>
          </div>
        )}

        {sections.map((s) => (
          <div key={s.label} style={{ marginBottom: 40 }}>
            <span className="ft-k" style={s.urgent ? { color: 'var(--ft-ink)' } : {}}>
              {s.label} · {s.items.length}
            </span>
            <div style={{ marginTop: 14 }}>
              {s.items.map((r) => (
                <ReviewRow key={r.id} review={r} onClick={() => navigate(`/analyst/reviews/${r.id}`)} />
              ))}
            </div>
          </div>
        ))}

        {reviews.length === 0 && (
          <div style={{ paddingTop: 80, textAlign: 'center' }}>
            <p style={{ fontSize: 17, color: 'var(--ft-ink-3)' }}>No reviews yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalystDashboard;
